import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

const PROVIDER = {
  "@type": "Organization",
  name: "GearCheckAuto",
  url: "https://gearcheckauto.com/",
};

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Auto repair",
    name: "Find auto repair near you",
    provider: PROVIDER,
    areaServed: { "@type": "Country", name: "United States" },
    url: "https://gearcheckauto.com/auto-repair",
    description:
      "Find a trustworthy auto repair shop or mobile mechanic near you. Send one request to several local shops, compare quotes and response times, and book the repair that fits.",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      { "@type": "ListItem", position: 2, name: "Auto repair", item: "https://gearcheckauto.com/auto-repair" },
    ],
  },
];

const SERVICES = [
  "Brakes, rotors, and pads",
  "Check engine light and diagnostics",
  "Oil changes and routine maintenance",
  "Suspension, steering, and alignment",
  "Battery, starter, and alternator",
  "Heating, AC, and cooling system",
  "Tires, exhaust, and more",
];

export default function AutoRepairPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Auto repair</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Get your car repaired without the runaround
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            Finding a trustworthy auto repair shop should not mean calling around all day. With
            GearCheckAuto you describe the problem once, send a single request to several nearby shops
            and mobile mechanics, then compare quotes and response times and book the one that fits.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Find a shop
            </button>
            <button
              type="button"
              onClick={() => onNavigate("prePurchaseInspection")}
              className="rounded-lg border border-blue-600 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
            >
              Buying a used car?
            </button>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 text-slate-700 dark:text-gray-200">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">How to find a repair shop you can trust</h2>
        <p className="mt-4 leading-relaxed">
          The hardest part of car repair is not the fix - it is knowing who to trust and what a fair
          price looks like. Calling shops one at a time is slow, and it is hard to compare answers.
          GearCheckAuto flips that around: you send one clear request and let local shops come to you
          with quotes and timelines, so you can compare real options side by side.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">How GearCheckAuto works</h2>
        <ol className="mt-4 list-decimal space-y-3 pl-6">
          <li>Describe the symptoms - our request coach helps you add the details shops need to give an accurate quote.</li>
          <li>Send one request to several nearby auto repair shops and mobile mechanics at once.</li>
          <li>Compare quotes and response times, then book the shop that fits your budget and schedule.</li>
        </ol>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Repairs and services shops can handle</h2>
        <ul className="mt-4 list-disc space-y-2 pl-6">
          {SERVICES.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Mobile mechanics come to you</h2>
        <p className="mt-4 leading-relaxed">
          If your car is hard to move or you would rather not wait at a shop, mobile mechanics on
          GearCheckAuto can come to your home or workplace for many common repairs and diagnostics.
          They appear on the map with the area they serve, so you can see who covers your location.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Buying a used car?</h2>
        <p className="mt-4 leading-relaxed">
          If you are shopping rather than repairing, a{" "}
          <SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">
            pre-purchase inspection
          </SpaLink>{" "}
          checks the vehicle from end to end before you buy. More answers are on our{" "}
          <SpaLink view="faq" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">FAQ page</SpaLink>.
        </p>
      </article>

      {/* CTA */}
      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white">Ready to get back on the road?</h2>
          <p className="mt-3 text-slate-300">
            Send one request and let nearby shops and mobile mechanics compete for your repair.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white hover:bg-red-500"
            >
              Get started
            </button>
            <button
              type="button"
              onClick={() => onNavigate("contact")}
              className="rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
            >
              Contact us
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
